import { SgDatePickerCustomEvent } from '@sg-bootstrap/components';
import { SgDatePicker } from '@sg-bootstrap/components/dist/react-library/src/components';
import type { IEmittedDate } from '@sg-bootstrap/components/dist/types/interfaces/dates';
import { isDefined } from '@sgme/fp';
import { OnKeyDown, OnKeyDownArg } from 'components/common-form/builder/form/node/field/Grid/Keys';
import { tryToDetectDateFormat } from 'components/common-form/builder/form/node/field/Grid/pasting';
import type { PresetTranslation } from 'components/shared/DatePicker/hooks';
import { useDisablePresets, useTranslatePreset } from 'components/shared/DatePicker/hooks';
import { isWeekend, nextMonday } from 'date-fns';
import { useCallbackRef } from 'hooks/useCallbackRef';
import { useLocalDateFormat } from 'hooks/useLocalDateFormat';
import { useUserNotification } from 'hooks/useUserNotification';
import { KeyboardEvent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { WorkspaceType } from 'state/workspaces/model';
import { addOrRemoveIfExists } from 'utils/array/array';
import { now } from 'utils/core/now';
import { parsePastedDateWithErrorMessage } from 'utils/date';

import type { DateCalculation } from './utils';
import {
  asIsoDate,
  fromTimestamp,
  supportedDateFormats,
  toTimestamp,
  toTimestamps,
  toTimestampsString,
  userInputToIsoDate,
} from './utils';

export type DatePickerSize = 'sm' | 'md' | 'lg' | 'xl';
export type DatePickerType = 'multi' | 'mono';
export interface DatePickerProps<Type extends DatePickerType = 'multi'> {
  id?: string;
  e2e?: string;
  tabId?: string;
  size?: DatePickerSize;
  className?: string;

  type: Type;
  values?: readonly (string | undefined | null)[];
  inputValue?: string;
  disabled?: boolean;
  isClearable?: boolean;
  allowWeekend?: boolean;
  blackoutDates?: readonly string[];
  availableDates?: readonly string[];

  maxDate?: string;
  minDate?: string;
  required?: boolean;
  isReadOnly?: boolean;
  hasError?: boolean;
  format?: string;
  defaultValues?: readonly (string | undefined | null)[];
  onChange?: Type extends 'multi' ? (selectedDate: readonly string[]) => void : (selectedDate: string | null) => void;
  onPresetChange?: (translatedPreset: PresetTranslation) => void;
  onKeyDown?: OnKeyDown;
  datePresets?: readonly DateCalculation[];
  datePresetTranslations?: readonly PresetTranslation[];
  align?: 'start' | 'end' | 'left' | 'right';

  tabIndex?: number;
  hidePresets?: boolean;

  onPaste?: (value: string) => void;
  isFocused?: boolean;
  isSelectAll?: boolean;
  isCellInactive?: boolean;
  workspaceType?: WorkspaceType;
  labelledBy?: string;
  ariaLabel?: string;
}

export function DatePicker<Type extends DatePickerType>({
  values = [],
  className,
  id,
  tabId,
  e2e,
  size = 'md',
  align = 'start',
  type,
  inputValue,
  allowWeekend = true,
  blackoutDates,
  isClearable,
  disabled,
  isReadOnly,
  onChange,
  hasError,
  minDate,
  maxDate,
  defaultValues,
  datePresetTranslations = [],
  datePresets = [],
  tabIndex,
  format,
  availableDates,
  hidePresets,
  onKeyDown: handleKeyDown,
  onPaste: handlePaste,
  isFocused,
  isSelectAll,
  onPresetChange,
  labelledBy,
  ariaLabel,
}: DatePickerProps<Type>) {
  const { formatMessage } = useIntl();
  const [dateFormat, locale] = useLocalDateFormat();
  const inputId = id + '-input';
  const definedValues = useMemo(() => values.filter(isDefined), [values]);
  const onChangeByType = useCallback(
    (newValues: readonly string[]) => {
      if (type === 'multi') {
        (onChange as ((values: readonly string[]) => void) | undefined)?.(newValues);
      } else {
        const monoOnChange = onChange as ((newValue: string | null) => void) | undefined;
        if (newValues.length === 0) {
          monoOnChange?.(null);
        } else {
          monoOnChange?.(newValues[newValues.length - 1]);
        }
      }
    },
    [onChange, type],
  );

  const onSelected = useCallback(
    ({ detail: { timestamp } }: CustomEvent<IEmittedDate>) => {
      const valueToBeAdded = fromTimestamp(timestamp);
      if (!definedValues.includes(valueToBeAdded)) {
        onChangeByType([...definedValues, valueToBeAdded]);
      }
    },
    [onChangeByType, definedValues],
  );

  const onUnSelected = useCallback(
    ({ detail: { timestamp } }: CustomEvent<IEmittedDate>) => {
      const valueToBeRemoved = fromTimestamp(timestamp);
      if (definedValues.includes(valueToBeRemoved)) {
        const newDates = definedValues.filter(date => date !== valueToBeRemoved);
        onChangeByType(newDates);
      }
    },
    [onChangeByType, definedValues],
  );

  const onClearInput = useCallback(() => {
    if (defaultValues) {
      onChangeByType(defaultValues.filter(isDefined));
    } else {
      onChangeByType([]);
    }
  }, [defaultValues, onChangeByType]);
  const translatedPresets = useTranslatePreset(datePresetTranslations);

  const onPresetClicked = useCallback(
    ({ detail: { preset } }: CustomEvent<{ preset: string }>) => {
      const index = translatedPresets.indexOf(preset);
      if (index !== -1) {
        onPresetChange?.(datePresetTranslations[index]);
        if (index < datePresets.length) {
          const computePreset = datePresets[index];
          let newDate = computePreset(now());
          if (!allowWeekend && isWeekend(newDate)) {
            newDate = nextMonday(newDate);
          }
          onChangeByType(
            type === 'mono' ? [asIsoDate(newDate)] : addOrRemoveIfExists(definedValues, asIsoDate(newDate)),
          );
        }
      }
    },
    [
      translatedPresets,
      datePresets,
      allowWeekend,
      onChangeByType,
      type,
      definedValues,
      onPresetChange,
      datePresetTranslations,
    ],
  );

  const appendDate = useCallback(
    (newValue: string | undefined) => {
      if (newValue !== undefined && !definedValues.includes(newValue)) {
        onChangeByType([...definedValues, newValue]);
      }
    },
    [definedValues, onChangeByType],
  );

  const notification = useUserNotification();

  const onPaste = useCallback(
    (pastedText: string) => {
      if (pastedText !== undefined) {
        try {
          const allDateFormats = supportedDateFormats(dateFormat);
          const candidates = pastedText.split(/\r\n|\r|\n/g).filter(x => x.length >= 3);
          const detectedFormat = tryToDetectDateFormat({
            dates: candidates,
            dateFormats: allDateFormats,
            formatMsg: formatMessage,
            onWarning: msg => notification.warn({ title: { id: 'toast.paste' }, content: [msg] }),
            onError: msg => notification.error({ title: { id: 'toast.paste' }, content: [msg] }),
          });
          if (detectedFormat !== null) {
            const dates = candidates.map(c => parsePastedDateWithErrorMessage(c, detectedFormat, formatMessage));
            onChangeByType(dates);
            const msg = formatMessage({ id: 'moreMenu.pasteFromClipboard.successDatesOnly' }, { count: dates.length });
            notification.success({ title: { id: 'toast.paste' }, content: [msg] });
          }
        } catch (err: any) {
          notification.error({ title: { id: 'toast.paste' }, content: [err?.message] });
        }
      }
    },
    [dateFormat, formatMessage, notification, onChangeByType],
  );

  const onValuePasted = useCallback(
    (e: { detail: string }) => {
      const usedPaste = handlePaste ?? onPaste;
      usedPaste(e.detail);
    },
    [onPaste, handlePaste],
  );
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSgDatePickerElement>) => {
      if (handleKeyDown) {
        const arg: OnKeyDownArg = {
          e: e,
          commit: () => {
            const newValue = (e.target as HTMLInputElement).value;
            const newDate = userInputToIsoDate(newValue, dateFormat);
            appendDate(newDate);
          },
        };
        handleKeyDown(arg);
      }
    },
    [handleKeyDown, dateFormat, appendDate],
  );

  const disablePresets = useDisablePresets(allowWeekend, blackoutDates, minDate, maxDate);

  const availableTimestamps = useMemo(
    () => (availableDates === undefined ? undefined : availableDates.map(toTimestamp).join(',')),
    [availableDates],
  );

  const updateRef = useCallback(
    (node: HTMLSgDatePickerElement) => {
      /** @todo remove next line after sg-date-picker has been updated to honor value attribute changes */
      node.disablePresets += ',';

      node.getInputHtmlElement().then((input: any) => {
        if (type !== 'multi' && inputValue !== undefined && isDefined(input)) {
          input.value = inputValue;
          input.focus();
        }
        if (isFocused) {
          input.focus();
          if (isSelectAll) {
            input.select();
          }
        }
      });

      if (isDefined(tabIndex) && !disabled) {
        const input = node.getElementsByClassName('dp__input');
        if (input !== null && input.length > 0) {
          input[0].setAttribute('tabIndex', tabIndex.toString());
        }
      }
    },
    [tabIndex, disabled, type, inputValue, isFocused, isSelectAll],
  );

  const handleCalendarClose = useCallback(
    (event: SgDatePickerCustomEvent<void>) => {
      const node = event.target;
      if (hasError) {
        setTimeout(() => {
          node?.setAttribute('validation-state', 'warning');
        }, 0);
      }
    },
    [hasError],
  );

  const { callback } = useCallbackRef(updateRef);
  const computedFormat = format ?? dateFormat;
  const componentId = [tabId, inputId].filter(isDefined).join('-');

  // remove validationMessage="" below
  // when Jason issue is solved
  // https://sgithub.fr.world.socgen/sgbootstrap/sg-bootstrap/issues/5309
  // Until then we force the sgDatePicker to not show it's internal error message
  return (
    <SgDatePicker
      name={id}
      ref={callback}
      closeAfterSelect={type === 'mono'}
      placeholder={computedFormat}
      key={componentId}
      disablePresets={availableTimestamps ? undefined : disablePresets}
      showClearButton={isClearable}
      validationState={hasError ? 'warning' : undefined}
      validationMessage=""
      availableDates={availableTimestamps}
      itemSelection={type === 'multi' ? 'multiple' : 'single'}
      defaultValue={definedValues ? toTimestampsString(definedValues) : undefined}
      value={definedValues ? toTimestamps(definedValues) : undefined}
      displaySelectedDates
      maxSelectedDatesDisplayed={1}
      classes={className ?? ''}
      input-id={componentId}
      data-e2e={e2e || id}
      size={size}
      onValuePasted={onValuePasted}
      onInputCleared={onClearInput}
      onDateSelected={onSelected}
      onDateUnSelected={onUnSelected}
      onCustomPresetClick={onPresetClicked}
      onKeyDown={onKeyDown}
      onCalendarClose={handleCalendarClose}
      acceptEmptyValue={!isClearable}
      disabled={disabled ?? false}
      readonly={isReadOnly ?? false}
      format={computedFormat}
      displayFormat={format}
      locale={locale}
      aria-label={ariaLabel}
      customPresets={hidePresets ? undefined : translatedPresets.join(',')}
      selectionColor="info"
      yearControllers
      // tab-index={!disabled ? tabIndex : undefined}
      // tabIndex={!disabled ? tabIndex : undefined}
      dropdownAlignment={align}
      labelledBy={labelledBy}
    />
  );
}
